import axios from "axios";
import { startProcess } from "./premiumToast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/** Rutas públicas que NUNCA deben redirigir a /login ante un 401 */
export const PUBLIC_PATHS = ["/login", "/forgot-password", "/reset-password"];

const _isPublicPage = () => PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

const api = axios.create({ baseURL: API });

// v101.175.0 · Tarjeta de PROCESO (estilo IA, con % de avance) en las llamadas a integraciones: aprovisionamiento,
//              cierres, cancelaciones, sincronización con Odoo, cliente en Odoo. Aparece si tarda más de 600 ms.
//              Para excluir una llamada: api.post(url, body, { noProcess: true }).
const PROCESS_RULES = [
  [/\/provisioning\/provision\/cancel|\/desaprovision|\/unlink/i, "desaprov"],
  [/\/orders\/[^/]+\/close\b|\/push-to-odoo|\/close-v2|\/send-close|\/fiscalization\/decision/i, "close"],
  [/\/provisioning\/(provision|discover|axxess|occupy-material-serial|odoo-sync|admin\/retry-sync|manual-materials-discharge|equipment-returns)/i, "provision"],
  [/\/refresh-from-odoo|\/odoo\/sync\/run|\/force-resync|\/odoo-live\/|\/sync\/|\/resync|\/sincronizacion-odoo\/reenviar|\/full-sync/i, "sync"],
  [/\/client-(address|coordinates)/i, "client"],
  [/\/cancel(?!-stop)\b|\/cancelar\b/i, "cancel"],
];
const _processKind = (cfg) => {
  if (!cfg || cfg.noProcess) return null;
  const m = String(cfg.method || "get").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(m)) return null;
  const url = String(cfg.url || "");
  const hit = PROCESS_RULES.find(([re]) => re.test(url));
  return hit ? hit[1] : null;
};
api.interceptors.request.use((config) => {
  const kind = _processKind(config);
  if (kind) {
    const st = { handle: null };
    st.timer = setTimeout(() => {
      try {
        st.handle = startProcess(kind);
      } catch (_e) { /* nunca romper la llamada */ }
    }, 600);
    config._afcProcess = st;
  }
  return config;
});
const _endProcess = (config, ok) => {
  const st = config && config._afcProcess;
  if (!st) return;
  clearTimeout(st.timer);
  try { if (st.handle) st.handle.finish(ok); } catch (_e) { /* */ }
  config._afcProcess = null;
};
api.interceptors.response.use(
  (r) => { _endProcess(r && r.config, true); return r; },
  (err) => { _endProcess(err && err.config, false); return Promise.reject(err); },
);

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("afc_token");
  // v79.0 · No adjuntar token a endpoints públicos de auth
  const isPublicAuth =
    config?.url?.includes("/auth/login") ||
    config?.url?.includes("/auth/refresh") ||
    config?.url?.includes("/auth/reset-password") ||
    config?.url?.includes("/auth/forgot-password");
  if (token && !isPublicAuth) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Refresh token automático en 401 (excepto en endpoints de auth)
let refreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
};

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // v3.9.9 · Normaliza `err.response.data.detail` cuando viene como array
    // de errores Pydantic 422 → string plano. Evita React #31 cuando cualquier
    // catch hace `toast.error(err.response.data.detail)` esperando un string.
    try {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        err.response.data._detail_raw = detail;
        err.response.data.detail = detail
          .map((d) => {
            if (typeof d === "string") return d;
            const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
            const msg = d?.msg || d?.message || JSON.stringify(d);
            return loc ? `${loc}: ${msg}` : msg;
          })
          .join(" · ");
      } else if (detail && typeof detail === "object") {
        err.response.data._detail_raw = detail;
        err.response.data.detail = detail.msg || detail.message || JSON.stringify(detail);
      }
    } catch (_normErr) { /* no-op: nunca romper el flow de axios */ }

    const original = err.config;
    const isAuthEndpoint =
      original?.url?.includes("/auth/login") ||
      original?.url?.includes("/auth/refresh") ||
      original?.url?.includes("/auth/logout") ||
      original?.url?.includes("/auth/reset-password") ||
      original?.url?.includes("/auth/forgot-password");

    // v101.27.0 · «Entrar como»: si el token temporal venció o se cerró, volver a la cuenta del super_admin
    if (err?.response?.status === 401 && !isAuthEndpoint && localStorage.getItem("afc_imp_backup")) {
      try {
        const b = JSON.parse(localStorage.getItem("afc_imp_backup") || "{}");
        if (b.token) localStorage.setItem("afc_token", b.token);
        if (b.refresh) localStorage.setItem("afc_refresh", b.refresh);
        if (b.user) localStorage.setItem("afc_user", JSON.stringify(b.user));
        sessionStorage.setItem("afc_imp_msg", "La sesión «Entrar como» terminó. Volviste a tu cuenta.");
      } catch { /* ignore */ }
      localStorage.removeItem("afc_imp_backup");
      localStorage.removeItem("afc_imp");
      window.location.href = "/admin/usuarios";
      return Promise.reject(err);
    }

    if (err?.response?.status === 401 && !original._retry && !isAuthEndpoint) {
      const refresh = localStorage.getItem("afc_refresh");
      if (!refresh) {
        // No refresh disponible — sesión perdida
        localStorage.removeItem("afc_token");
        localStorage.removeItem("afc_user");
        if (!_isPublicPage()) {
          window.location.href = "/login";
        }
        return Promise.reject(err);
      }

      if (refreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then((token) => {
            original.headers.Authorization = `Bearer ${token}`;
            return api(original);
          })
          .catch((e) => Promise.reject(e));
      }

      original._retry = true;
      refreshing = true;

      try {
        const { data } = await axios.post(`${API}/v1/auth/refresh`, {
          refresh_token: refresh,
        });
        localStorage.setItem("afc_token", data.access_token);
        localStorage.setItem("afc_refresh", data.refresh_token);
        original.headers.Authorization = `Bearer ${data.access_token}`;
        processQueue(null, data.access_token);
        return api(original);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        localStorage.removeItem("afc_token");
        localStorage.removeItem("afc_refresh");
        localStorage.removeItem("afc_user");
        if (!_isPublicPage()) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshErr);
      } finally {
        refreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

export default api;

/**
 * v3.9.9 · Sanitiza cualquier error de axios/FastAPI a un string plano seguro
 * de pasar a toast.error / <div> / etc. Evita React #31 cuando FastAPI/Pydantic
 * devuelve `detail` como array de objetos {type, loc, msg, input}.
 */
export function extractApiError(err, fallback = "Ocurrió un error") {
  // Si ya es string, retornar directo
  if (typeof err === "string") return err;
  if (!err) return fallback;

  const detail = err?.response?.data?.detail ?? err?.data?.detail ?? err?.detail;

  // Caso 1: Pydantic 422 → array de errores
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
        const msg = d?.msg || d?.message || JSON.stringify(d);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join(" · ");
  }

  // Caso 2: objeto simple con msg
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }

  // Caso 3: string plano
  if (typeof detail === "string") return detail;

  // Caso 4: axios genérico
  if (err?.message) return err.message;

  return fallback;
}
